/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * STH-50 — shipping status (Ready to ship / Pre Order).
 *
 * Liquid renders the correct state on first paint; this keeps it in sync as the customer
 * changes variant. It deliberately lives here rather than in component-product-variants.js:
 * that file is Mandolin's, AU is on 2.15 and UK on 2.20, so editing it would conflict on every
 * vendor upgrade and on the main -> store-uk sync. `variant:changed` is the theme's own
 * documented hook. The adapter keeps the existing button and size controls mounted so
 * availability changes preserve focus and do not restart their visual state.
 */
(function () {
  function readProductData(scope) {
    var el = scope.querySelector('[data-product-json]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // Pre-orders sell confirmed, tracked quantities. Untracked regular products (e.g. gift
  // cards) retain Shopify availability; their inventory_quantity is not a stock count.
  function isInStock(inventory, preorder) {
    if (!inventory) return false;
    if (!inventory.inventory_management) return !preorder;
    return inventory.inventory_quantity > 0;
  }

  function setButtonLabel(button, label) {
    var span = button.querySelector('span');
    if (!span) {
      span = document.createElement('span');
      button.innerHTML = '';
      button.appendChild(span);
    }
    span.textContent = label;
  }

  function syncVariantInput(scope, variant) {
    scope.querySelectorAll('[name="id"]').forEach(function (input) {
      if (input.tagName === 'SELECT') {
        var selected = Array.from(input.options).find(function (option) {
          return variant && option.value === String(variant.id);
        });
        if (selected) selected.selected = true;
        else {
          Array.from(input.options).forEach(function (option) { option.selected = false; });
          input.selectedIndex = -1;
        }
      } else {
        input.value = variant ? variant.id : '';
      }
    });
  }

  function syncOptionLabels(scope) {
    scope.querySelectorAll('.ProductForm__Option').forEach(function (option) {
      var selected = Array.from(option.querySelectorAll('input[type="radio"]')).find(function (input) {
        return input.checked;
      }) || option.querySelector('select');
      var label = option.querySelector('.ProductForm__SelectedValue');
      if (selected && label) label.textContent = selected.value;
    });
  }

  // Update availability decoration only. Replacing this block would discard the
  // customer's focused select/radio and can close the menu during a later choice.
  function refreshPicker(scope, html) {
    var source = html.querySelector('.Product__Block--variantPicker');
    var destination = scope.querySelector('.Product__Block--variantPicker');
    if (!source || !destination) return;
    destination.querySelectorAll('select').forEach(function (select) {
      var fresh = Array.from(source.querySelectorAll('select')).find(function (item) {
        return item.id === select.id;
      });
      if (!fresh || select.name === 'id') return;
      Array.from(select.options).forEach(function (option) {
        var freshOption = Array.from(fresh.options).find(function (item) {
          return item.value === option.value;
        });
        if (freshOption && option.textContent !== freshOption.textContent) {
          option.textContent = freshOption.textContent;
        }
      });
    });
    destination.querySelectorAll('label[for]').forEach(function (label) {
      var fresh = Array.from(source.querySelectorAll('label[for]')).find(function (item) {
        return item.getAttribute('for') === label.getAttribute('for');
      });
      if (fresh) label.classList.toggle('Unavailable', fresh.classList.contains('Unavailable'));
    });
  }

  // Mandolin updates the button before its section request updates the form ID.
  // Keep that ID current immediately and apply only the latest section response.
  // This adapter preserves the vendor's refresh targets without changing its file.
  function patchVariantRefresh(prototype) {
    if (!prototype || prototype.pdVariantRefresh) return;
    var originalRefresh = prototype._updateProductInfo;
    var originalButtonUpdate = prototype._updateAddToCartButton;
    var originalHistoryUpdate = prototype._updateHistoryState;
    prototype.pdVariantRefresh = true;
    prototype._updateAddToCartButton = function (variant) {
      var data = readProductData(this.element);
      if (!data || !data.preorder) return originalButtonUpdate.call(this, variant);
      var button = this.element.querySelector('.ProductForm__AddToCart');
      if (button) {
        var primary = Boolean(variant && variant.available && !this.options.showPaymentButton);
        button.classList.toggle('Button--primary', primary);
        button.classList.toggle('Button--outline', !primary);
      }
      updateShippingStatus(this.element, variant);
    };
    prototype._updateHistoryState = function (variant) {
      var data = readProductData(this.element);
      if (!data || !data.preorder) return originalHistoryUpdate.call(this, variant);
      // Keep preview keys and other existing parameters while updating the size.
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    };
    prototype._updateProductInfo = function (variant) {
      var engine = this;
      var scope = engine.element;
      var data = readProductData(scope);
      if (!data || !data.preorder) return originalRefresh.call(engine, variant);

      var request = engine.pdVariantRequest = (engine.pdVariantRequest || 0) + 1;
      syncVariantInput(scope, variant);
      syncOptionLabels(scope);
      if (!variant) return originalRefresh.call(engine, variant);

      return fetch(engine.productData.url + '?variant=' + variant.id + '&section_id=' + engine.sectionId)
        .then(function (response) {
          if (!response.ok) throw new Error('Product section request failed');
          return response.text();
        }).then(function (content) {
          if (request !== engine.pdVariantRequest || !scope.isConnected) return;
          var html = new DOMParser().parseFromString(content, 'text/html');
          function replace(selector, copyDisplay) {
            var source = html.querySelector(selector);
            var destination = scope.querySelector(selector);
            if (source && destination) {
              if (destination.innerHTML !== source.innerHTML) destination.innerHTML = source.innerHTML;
              if (copyDisplay) destination.style.display = source.style.display;
            }
          }
          replace('.Product__PriceList');
          if (engine.options.showSku) replace('.Product__Sku', true);
          replace('.Product__UnitPrice', true);
          replace('.Product__PayInstallments');
          refreshPicker(scope, html);

          syncVariantInput(scope, variant);
        }).catch(function () {
          // The selected ID and stock rules remain correct even if the optional
          // price/picker refresh fails. A later size change can retry normally.
        });
    };
  }

  function installVariantRefresh(event) {
    var component = window.themegoal && window.themegoal.components_business &&
      window.themegoal.components_business.product_variants;
    if (component) patchVariantRefresh(component.ProductVariants.prototype);

    // Product and quick-view scripts can each load the vendor class. An existing
    // product section may therefore use a different prototype from the global one.
    var scopes = event ? [event.target.closest('[data-section-id]')] :
      document.querySelectorAll('[data-section-id]');
    scopes.forEach(function (scope) {
      if (scope && scope.productVariants) {
        patchVariantRefresh(Object.getPrototypeOf(scope.productVariants));
      }
    });
  }

  function updateShippingStatus(scope, variant) {
    var data = readProductData(scope);
    if (!data || !data.preorder) return;

    var preorder = data.preorder || {};
    var inventories = data.inventories || {};
    var inventory = variant ? inventories[variant.id] : null;
    var purchasable = Boolean(variant && variant.available && isInStock(inventory, preorder.enabled));
    var isPreorderPurchase = Boolean(preorder.enabled) && purchasable;

    var state = '';
    if (purchasable) {
      state = isPreorderPurchase ? 'preorder' : 'ready';
    }

    // Inventory copy is rendered per variant in Liquid, preserving translations and
    // pluralisation. Update it now, even when the optional section request fails.
    var inventoryEl = scope.querySelector('.ProductForm__Inventory');
    if (inventoryEl) {
      var inventoryMessage = purchasable && inventory ? inventory.inventory_message || '' : '';
      inventoryEl.textContent = inventoryMessage;
      inventoryEl.style.display = inventoryMessage ? '' : 'none';
    }

    // Update the existing nodes immediately; background pricing refreshes keep them mounted.
    var statusEl = scope.querySelector('[data-shipping-status]');
    var panelState = state || (variant ? 'soldout' : 'unavailable');
    if (statusEl) {
      var titleEl = statusEl.querySelector('[data-shipping-status-title]');
      var textEl = statusEl.querySelector('[data-shipping-status-text]');
      if (titleEl) {
        titleEl.textContent = statusEl.getAttribute('data-' + panelState + '-title') || '';
      }
      if (textEl) {
        textEl.textContent = statusEl.getAttribute('data-' + panelState + '-text') || '';
      }
    }
    if (statusEl) {
      statusEl.setAttribute('data-state', panelState);
      // The neutral availability message occupies the same panel as shipping copy.
      statusEl.style.display = '';
    }

    scope.querySelectorAll('[data-shipping-badge]').forEach(function (badge) {
      badge.style.display = state ? '' : 'none';
      badge.classList.toggle('ProductShippingBadge--preorder', state === 'preorder');
      badge.classList.toggle('ProductShippingBadge--ready', state === 'ready');
      badge.parentElement.classList.toggle('Form__Item--withShippingBadge', Boolean(state));
      var text = badge.querySelector('[data-shipping-badge-text]');
      if (text) text.textContent = state ? badge.getAttribute('data-' + state + '-label') || '' : '';
    });

    var button = scope.querySelector('.ProductForm__AddToCart');
    if (button) {
      button.disabled = !purchasable;
      if (purchasable) button.setAttribute('data-action', 'add-to-cart');
      else button.removeAttribute('data-action');
      var label = !variant ? preorder.unavailable_label : !purchasable ? preorder.sold_out_label :
        isPreorderPurchase ? preorder.button_label : preorder.add_to_cart_label;
      if (label) setButtonLabel(button, label);
      button.classList.toggle('Button--preorder', isPreorderPurchase);
      if (!purchasable) {
        button.classList.remove('Button--primary');
        button.classList.add('Button--outline');
      }
    }

    var checkout = scope.querySelector('[data-shipping-checkout]');
    if (checkout) checkout.style.display = purchasable ? '' : 'none';
  }

  document.addEventListener('variant:changed', function (event) {
    var scope = event.target.closest('[data-section-id]');
    if (!scope || !event.detail) return;
    updateShippingStatus(scope, event.detail.variant);
  });

  installVariantRefresh();
  // Quick-view and editor sections can load the vendor component after custom.js.
  // Install before their delegated change handler starts the first request.
  document.addEventListener('change', installVariantRefresh, true);
})();
